import React, { useState, useEffect } from 'react';
import { db, storage, auth } from '../../firebase/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import WIcon from '../shared/WIcon';

const MAX_IMAGES = 7;

const initialsFor = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const VendorProfileForm = () => {
  const [profile, setProfile] = useState({
    name: '', description: '', location: '', contactInfo: '', website: '', serviceHours: '',
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [mainPhotoId, setMainPhotoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    const fetch = async () => {
      if (!auth.currentUser) return;
      try {
        const snap = await getDoc(doc(db, 'vendors', auth.currentUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          setProfile({
            name: data.name || data.businessName || '',
            description: data.description || '',
            location: data.location || '',
            contactInfo: data.contactInfo || '',
            website: data.website || '',
            serviceHours: data.serviceHours || '',
          });
          if (data.logoUrl) {
            setExistingImages([{ id: 'logo', url: data.logoUrl, name: 'Logo' }]);
          } else if (Array.isArray(data.images)) {
            setExistingImages(data.images);
          }
          if (data.mainPhotoId) setMainPhotoId(data.mainPhotoId);
        }
      } catch (err) {
        console.error('Error loading vendor profile:', err);
        setMessage({ text: 'Error loading profile data.', type: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };
    fetch();
  }, []);

  const handleChange = (e) => {
    setProfile((p) => ({ ...p, [e.target.name]: e.target.value }));
    if (message.text) setMessage({ text: '', type: '' });
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (existingImages.length + imageFiles.length + files.length > MAX_IMAGES) {
      setMessage({ text: `You can only upload up to ${MAX_IMAGES} images.`, type: 'error' });
      return;
    }
    setImageFiles((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, { id: `new-${Date.now()}-${file.name}`, url: reader.result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    if (message.text) setMessage({ text: '', type: '' });
  };

  const removeNew = (id) => {
    const idx = imagePreviews.findIndex((p) => p.id === id);
    if (idx === -1) return;
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeExisting = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
    if (mainPhotoId === id) setMainPhotoId(null);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      if (!auth.currentUser) throw new Error('Not authenticated.');
      const vendorId = auth.currentUser.uid;

      let updatedImages = [...existingImages];
      for (const file of imageFiles) {
        const filename = `vendor-images/${vendorId}-${Date.now()}-${file.name}`;
        const imageRef = ref(storage, filename);
        await uploadBytes(imageRef, file);
        const url = await getDownloadURL(imageRef);
        updatedImages.push({
          id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          url, name: file.name, path: filename,
        });
      }

      let nextMain = mainPhotoId;
      if (!updatedImages.find((img) => img.id === nextMain)) {
        nextMain = updatedImages[0]?.id || null;
      }

      await setDoc(doc(db, 'vendors', vendorId), {
        ...profile,
        images: updatedImages,
        mainPhotoId: nextMain,
        email: auth.currentUser.email,
        updatedAt: new Date(),
        ...(updatedImages.length > 0 ? { logoUrl: null } : {}),
      }, { merge: true });

      setExistingImages(updatedImages);
      setMainPhotoId(nextMain);
      setImageFiles([]);
      setImagePreviews([]);
      setMessage({ text: 'Profile updated successfully.', type: 'success' });
    } catch (err) {
      console.error('Error saving profile:', err);
      setMessage({ text: err.message || 'Error updating profile.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <div style={{ padding: 40, color: '#64748b', fontSize: 14 }}>Loading profile…</div>;
  }

  const totalImages = existingImages.length + imageFiles.length;
  const imagesRemaining = MAX_IMAGES - totalImages;
  const mainImage = existingImages.find((img) => img.id === mainPhotoId) || existingImages[0];

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* Form */}
      <form onSubmit={saveProfile} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {message.text && (
          <div className={`loysta-alert ${message.type === 'success' ? 'success' : 'error'}`}>{message.text}</div>
        )}

        <div className="loysta-card" style={{ padding: 0 }}>
          <div className="loysta-card-head">
            <span className="loysta-card-title">Business profile</span>
          </div>
          <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label>Business name *</label>
              <input className="form-control" type="text" name="name" value={profile.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-control"
                name="description"
                value={profile.description}
                onChange={handleChange}
                placeholder="Tell employees what you offer"
                rows={3}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label>Location</label>
                <input className="form-control" type="text" name="location" value={profile.location} onChange={handleChange} placeholder="Address or service area" />
              </div>
              <div className="form-group">
                <label>Contact</label>
                <input className="form-control" type="text" name="contactInfo" value={profile.contactInfo} onChange={handleChange} placeholder="Phone or email" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label>Website</label>
                <input className="form-control" type="url" name="website" value={profile.website} onChange={handleChange} placeholder="https://" />
              </div>
              <div className="form-group">
                <label>Business hours</label>
                <input className="form-control" type="text" name="serviceHours" value={profile.serviceHours} onChange={handleChange} placeholder="Mon–Fri 9–6" />
              </div>
            </div>
          </div>
        </div>

        <div className="loysta-card" style={{ padding: 0 }}>
          <div className="loysta-card-head">
            <span className="loysta-card-title">Photos</span>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
              {totalImages} / {MAX_IMAGES} used · {imagesRemaining} remaining
            </span>
          </div>
          <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {imagesRemaining > 0 ? (
              <label
                htmlFor="images"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 16px',
                  background: '#f8fafc',
                  border: '1.5px dashed #cbd5e1',
                  borderRadius: 10,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00bf63'; e.currentTarget.style.background = 'rgba(0,191,99,0.04)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(0,191,99,0.10)', color: '#00bf63',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <WIcon name="plus" size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Upload photos</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>JPG or PNG. Pick a "main" photo employees will see first.</div>
                </div>
                <input
                  id="images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  style={{ display: 'none' }}
                />
              </label>
            ) : (
              <div style={{
                padding: 12, borderRadius: 10,
                background: '#f8fafc', border: '1px solid #e2e8f0',
                color: '#64748b', fontSize: 13,
              }}>
                Maximum reached — remove a photo to add another.
              </div>
            )}

            {(existingImages.length > 0 || imagePreviews.length > 0) && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 12,
              }}>
                {existingImages.map((image) => (
                  <ImageTile
                    key={image.id}
                    src={image.url}
                    isMain={mainPhotoId === image.id}
                    onSetMain={() => setMainPhotoId(image.id)}
                    onRemove={() => removeExisting(image.id)}
                  />
                ))}
                {imagePreviews.map((preview) => (
                  <ImageTile
                    key={preview.id}
                    src={preview.url}
                    isNew
                    onRemove={() => removeNew(preview.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ minWidth: 160, justifyContent: 'center' }}
          >
            {loading ? 'Saving…' : (
              <>
                <WIcon name="check" size={14} color="white" />
                Save profile
              </>
            )}
          </button>
        </div>
      </form>

      {/* Right: account meta */}
      <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="loysta-card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            {mainImage ? (
              <img
                src={mainImage.url}
                alt={profile.name || 'Business'}
                style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: 'linear-gradient(135deg, #00bf63, #c1ff72)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 800, color: '#0f172a', flexShrink: 0,
              }}>{initialsFor(profile.name)}</div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profile.name || '—'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Vendor account</div>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
            <Row label="Sign-in" value="Google SSO" />
            <Row label="Email" value={auth.currentUser?.email || '—'} mono />
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.55, marginTop: 14, marginBottom: 0 }}>
            Need to change the sign-in email? Contact your platform administrator.
          </p>
        </div>

        <div style={{
          background: '#0f172a',
          borderRadius: 12,
          padding: 18,
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: -20, right: -20,
            width: 100, height: 100, borderRadius: '50%',
            background: 'rgba(193,255,114,0.07)', pointerEvents: 'none',
          }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 4 }}>
            Tip
          </div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, margin: 0 }}>
            Choose a clear "main" photo of your storefront or product — it's the first thing employees see in the app.
          </p>
        </div>
      </div>
    </div>
  );
};

const ImageTile = ({ src, isMain, isNew, onSetMain, onRemove }) => (
  <div style={{
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    border: `1.5px solid ${isMain ? '#00bf63' : '#e2e8f0'}`,
    aspectRatio: '4 / 3',
    background: '#f1f5f9',
  }}>
    <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

    {(isMain || isNew) && (
      <div style={{
        position: 'absolute',
        top: 8, left: 8,
        padding: '3px 9px',
        borderRadius: 999,
        fontSize: 11, fontWeight: 700,
        background: isMain ? 'rgba(0,191,99,0.95)' : 'rgba(15,23,42,0.85)',
        color: 'white',
      }}>
        {isMain ? 'MAIN' : 'NEW'}
      </div>
    )}

    <div style={{
      position: 'absolute',
      bottom: 0, left: 0, right: 0,
      padding: 8,
      background: 'linear-gradient(0deg, rgba(0,0,0,0.7), transparent)',
      display: 'flex',
      gap: 6,
      justifyContent: 'flex-end',
    }}>
      {!isNew && !isMain && onSetMain && (
        <button
          type="button"
          onClick={onSetMain}
          style={{
            padding: '4px 10px',
            background: 'rgba(255,255,255,0.95)',
            color: '#0f172a',
            border: 'none',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >Set main</button>
      )}
      <button
        type="button"
        onClick={onRemove}
        style={{
          padding: '4px 8px',
          background: 'rgba(239,68,68,0.95)',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >Remove</button>
    </div>
  </div>
);

const Row = ({ label, value, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ color: '#94a3b8' }}>{label}</span>
    <span style={{
      color: '#0f172a',
      fontWeight: 600,
      fontFamily: mono ? 'SF Mono, ui-monospace, Menlo, monospace' : 'inherit',
      fontSize: mono ? 12 : 13,
      maxWidth: 180,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}>{value}</span>
  </div>
);

export default VendorProfileForm;
